import type { ParsedUrlQuery } from 'querystring'
import type {
    Amenity,
    Category,
    Filters,
    ListingSummary,
    SearchOptions,
    SortKey
} from '@/types'

export const emptySearchOptions: SearchOptions = { areas: [], types: [], prices: [] }

export const SORT_OPTIONS: Array<{ value: SortKey, label: string }> = [
    { value: 'recommended', label: 'Recommended' },
    { value: 'newest', label: 'Newest' },
    { value: 'price-asc', label: 'Price: low to high' },
    { value: 'price-desc', label: 'Price: high to low' },
    { value: 'size', label: 'Largest first' }
]

export const emptyFilters: Filters = {
    q: '',
    area: '',
    category: '',
    styles: [],
    types: [],
    amenities: [],
    minPrice: null,
    maxPrice: null,
    beds: 0,
    baths: 0,
    sort: 'recommended'
}

const first = (value: string | string[] | undefined): string => (Array.isArray(value) ? value[0] : value) ?? ''

const list = (value: string | string[] | undefined): string[] =>
    first(value).split(',').map(item => item.trim().toLowerCase()).filter(Boolean)

const positive = (value: string | string[] | undefined): number | null => {
    const number = Number(first(value))

    return Number.isFinite(number) && number > 0 ? Math.round(number) : null
}

export const parseFilters = (query: ParsedUrlQuery): Filters => {
    const sort = first(query.sort) as SortKey

    return {
        q: first(query.q).trim().slice(0, 80),
        area: first(query.area).trim().toLowerCase(),
        category: first(query.category).trim().toLowerCase(),
        styles: list(query.style),
        types: list(query.type),
        amenities: list(query.amenities),
        minPrice: positive(query.min),
        maxPrice: positive(query.max),
        beds: positive(query.beds) ?? 0,
        baths: positive(query.baths) ?? 0,
        sort: SORT_OPTIONS.some(option => option.value === sort) ? sort : 'recommended'
    }
}

export const filtersToQuery = (filters: Filters): Record<string, string> => {
    const query: Record<string, string> = {}

    if (filters.q) query.q = filters.q
    if (filters.area) query.area = filters.area
    if (filters.category) query.category = filters.category
    if (filters.styles.length) query.style = filters.styles.join(',')
    if (filters.types.length) query.type = filters.types.join(',')
    if (filters.amenities.length) query.amenities = filters.amenities.join(',')
    if (filters.minPrice) query.min = String(filters.minPrice)
    if (filters.maxPrice) query.max = String(filters.maxPrice)
    if (filters.beds) query.beds = String(filters.beds)
    if (filters.baths) query.baths = String(filters.baths)
    if (filters.sort !== 'recommended') query.sort = filters.sort

    return query
}

export const homesHref = (filters: Partial<Filters>): string => {
    const params = new URLSearchParams(filtersToQuery({ ...emptyFilters, ...filters }))
    const search = params.toString()

    return search ? `/homes?${search}` : '/homes'
}

export const countPanelFilters = (filters: Filters): number =>
    filters.styles.length
    + filters.types.length
    + filters.amenities.length
    + (filters.minPrice || filters.maxPrice ? 1 : 0)
    + (filters.beds ? 1 : 0)
    + (filters.baths ? 1 : 0)
    + (filters.area ? 1 : 0)

type Lookups = {
    categories: Category[]
    amenities: Amenity[]
}

const matchesText = (listing: ListingSummary, text: string): boolean => {
    const needle = text.toLowerCase()

    return [
        listing.title,
        listing.neighborhood,
        listing.city,
        listing.area?.name,
        listing.style?.name,
        listing.property_type
    ].some(value => value?.toLowerCase().includes(needle))
}

export const applyFilters = (listings: ListingSummary[], filters: Filters, lookups: Lookups): ListingSummary[] => {
    const categoryId = filters.category
        ? lookups.categories.find(category => category.slug === filters.category)?.id ?? '__none__'
        : null

    const amenityIds = filters.amenities
        .map(slug => lookups.amenities.find(amenity => amenity.slug === slug)?.id ?? '__none__')

    const filtered = listings.filter(listing => {
        if (filters.q && !matchesText(listing, filters.q)) return false
        if (filters.area && listing.area?.slug !== filters.area) return false
        if (categoryId && !listing.category_ids.includes(categoryId)) return false
        if (filters.styles.length && !filters.styles.includes(listing.style?.slug ?? '')) return false
        if (filters.types.length && !filters.types.includes(listing.property_type.trim().toLowerCase())) return false
        if (amenityIds.some(id => !listing.amenity_ids.includes(id))) return false
        if (filters.minPrice && listing.price < filters.minPrice) return false
        if (filters.maxPrice && listing.price > filters.maxPrice) return false
        if (filters.beds && listing.bedrooms < filters.beds) return false
        if (filters.baths && listing.bathrooms < filters.baths) return false

        return true
    })

    return sortListings(filtered, filters.sort)
}

export const sortListings = (listings: ListingSummary[], sort: SortKey): ListingSummary[] => {
    const sorted = [...listings]

    switch (sort) {
        case 'price-asc':
            return sorted.sort((a, b) => a.price - b.price)
        case 'price-desc':
            return sorted.sort((a, b) => b.price - a.price)
        case 'newest':
            return sorted.sort((a, b) => (b.listed_at ?? '').localeCompare(a.listed_at ?? ''))
        case 'size':
            return sorted.sort((a, b) => (b.area_sqft ?? 0) - (a.area_sqft ?? 0))
        default:
            return sorted.sort((a, b) => Number(b.is_featured) - Number(a.is_featured) || (b.listed_at ?? '').localeCompare(a.listed_at ?? ''))
    }
}

export const isWithinBounds = (
    listing: ListingSummary,
    bounds: { north: number, south: number, east: number, west: number }
): boolean => {
    if (listing.latitude === null || listing.longitude === null) return false

    return listing.latitude <= bounds.north
        && listing.latitude >= bounds.south
        && listing.longitude <= bounds.east
        && listing.longitude >= bounds.west
}
